// ---------------------------------------------------------------------------
// Tool catalog (Agent Coverage table)
// ---------------------------------------------------------------------------
// Moved verbatim out of aiUsageApi.ts.

import { adminGet, adminSend } from './transport';

export type ToolCoverage = 'full' | 'partial' | 'stub' | 'unknown';

export interface ToolCatalogRow {
  id: string;
  name: string;
  description: string;
  domain: string;
  coverage: ToolCoverage;
  status: 'active' | 'disabled';
  requiredFeature: string;
  path: string;
  method: string;
  version: number;
}

export interface ToolCatalogDomain {
  domain: string;
  label: string;
  toolCount: number;
  tools: ToolCatalogRow[];
}

export interface ToolCatalogInventorySummary {
  totalEndpoints: number;
  totalServices: number;
  totalGaps: number;
  byMethod: Record<string, number>;
  lastScanAt: number; // epoch ms, 0 if scanner has never run
  /** Phase 2 additions. Missing on older backends. */
  totalEntities?: number;
  entitiesByKind?: Record<string, number>;
  totalFields?: number;
  /** Phase 3 — entities with ≥1 tool via heuristic path match. */
  coveredEntities?: number;
}

export interface ToolCatalogEntity {
  name: string;
  kind: string;
  fieldCount: number;
  sourceFile: string;
  covered: boolean;
}

export interface ToolCatalogDrift {
  toolId: string;
  kind: 'endpoint_removed' | 'path_drift';
  detail: string;
  method: string;
  path: string;
  domain: string;
  coverage: string;
}

export interface ToolEditableFields {
  name?: string;
  description?: string;
  inputSchema?: any;
  method?: string;
  baseUrl?: string;
  baseUrlEnv?: string;
  path?: string;
  paramMap?: Record<string, 'path' | 'query' | 'body' | 'header'>;
  requiredFeature?: string;
  domain?: string;
  coverage?: 'full' | 'partial' | 'stub' | 'unknown';
  status?: 'active' | 'disabled';
}

export interface ToolCatalogGap {
  sk: string;            // e.g. "GET:/reports/foo"
  method: string;
  path: string;
  service: string;
  servicePath: string;
  sourceFile: string;
  domain: string;
  detectedAt: number;
  lastSeenAt: number;
}

export interface ToolCatalogModule {
  id: string;
  label: string;
  domains: string[];
  toolCount: number;
  endpointCount: number;
  coveragePct: number;
  status: 'full' | 'partial' | 'stub' | 'none' | 'skipped';
}

export interface ToolCatalog {
  totals: {
    total: number;
    full: number;
    partial: number;
    stub: number;
    disabled: number;
    domainsCovered: number;
    domainsTotal: number;
  };
  domains: ToolCatalogDomain[];
  /** Added in Phase 1 Coverage Awareness work — may be absent on older
   *  backends, so always narrow with `?.`. */
  inventory?: ToolCatalogInventorySummary;
  gaps?: ToolCatalogGap[];
  /** Added in Phase 2 — module × domain coverage matrix. */
  modules?: ToolCatalogModule[];
  /** Added in Phase 3 — full entity list with covered flag. */
  entities?: ToolCatalogEntity[];
  /** Added in Phase 4 — tools whose backend endpoint has moved/disappeared. */
  drift?: ToolCatalogDrift[];
}

export interface BulkStubBody {
  domains?: string[];
  inventorySks?: string[];
  requiredFeature?: string;
}

export interface BulkStubResponse {
  created: number;
  skipped: number;
  failed: number;
  tools: Array<{ id: string; method: string; path: string; domain: string }>;
}

export async function generateStubsBulk(body: BulkStubBody): Promise<BulkStubResponse> {
  return adminSend<BulkStubResponse>('/agents/admin/tool-catalog/stub-bulk', 'POST', body);
}

export async function updateTool(toolId: string, body: ToolEditableFields): Promise<{ tool: any }> {
  return adminSend<{ tool: any }>(`/agents/admin/tool-catalog/${encodeURIComponent(toolId)}`, 'PUT', body);
}

export async function deleteTool(toolId: string): Promise<{ deleted: boolean }> {
  return adminSend<{ deleted: boolean }>(`/agents/admin/tool-catalog/${encodeURIComponent(toolId)}`, 'DELETE');
}

/**
 * POST /agents/admin/tool-catalog/stub
 *
 * Promotes a discovered INVENTORY endpoint to a draft TOOL row with
 * coverage=stub. Write methods (POST/PUT/DELETE) require allowWriteMethod.
 */
export interface GenerateStubBody {
  inventorySk: string;
  baseUrlEnv?: string;
  requiredFeature?: string;
  allowWriteMethod?: boolean;
}

export interface GenerateStubResponse {
  tool: Record<string, any>;
  alreadyExists: boolean;
}

export async function generateToolStub(body: GenerateStubBody): Promise<GenerateStubResponse> {
  return adminSend<GenerateStubResponse>('/agents/admin/tool-catalog/stub', 'POST', body);
}

export async function getToolCatalog(): Promise<ToolCatalog> {
  return adminGet<ToolCatalog>('/agents/admin/tool-catalog');
}
